import React from 'react';
import { Check, Plus, X } from 'lucide-react';
import { Dialog, DialogContent } from '../ui/arc/dialog';
import { Button } from '../ui/arc/button';
import { Input } from '../ui/arc/input';
import { Textarea } from '../ui/arc/textarea';
import { ChipGroup } from '../ui/arc/chip-group';
import { PickerSelect } from '../ui/PickerSelect';
import { MoneyField } from './costsUi';
import { EXPENSE_CATEGORIES, PAYMENT_LABELS } from './GastosView';

// Popup de alta/edición de gasto. Solo UI: el estado del formulario y el guardado
// (handleSaveExpense / handleAddProvider) siguen en Costs.tsx.

export interface GastoForm { amount: string; provider_id: string; payment_method: string; notes: string; category: string; expense_date: string }

const fieldLabel = 'mb-2 block text-[length:var(--text-sm)] font-medium text-ink';
const PAYMENT_OPTIONS = Object.entries(PAYMENT_LABELS).map(([value, label]) => ({ value, label }));

export function GastoDialog({ open, editing, form, onChange, providers, showNewProvider, setShowNewProvider, newProviderName, setNewProviderName, onAddProvider, onSave, onClose }: {
    open: boolean; editing: boolean;
    form: GastoForm; onChange: (patch: Partial<GastoForm>) => void;
    providers: { id: number; name: string }[];
    showNewProvider: boolean; setShowNewProvider: (v: boolean) => void;
    newProviderName: string; setNewProviderName: (v: string) => void;
    onAddProvider: () => void; onSave: () => void; onClose: () => void;
}) {
    return (
        <Dialog open={open} onOpenChange={o => { if (!o) onClose(); }}>
            <DialogContent title={editing ? 'Editar gasto' : 'Nuevo gasto'}
                description="Se imputa en Resultados al mes de la fecha del gasto."
                className="w-[min(560px,calc(100vw-32px))]"
                // Con un monto cargado, un click afuera no tira lo escrito (Escape o Cancelar sí cierran).
                onPointerDownOutside={e => { if (form.amount) e.preventDefault(); }}>
                <form className="grid gap-5" onSubmit={e => { e.preventDefault(); if (form.amount) onSave(); }}>
                    <div className="grid gap-4 sm:grid-cols-2">
                        <div>
                            <label className={fieldLabel} htmlFor="gasto-monto">Monto *</label>
                            <MoneyField id="gasto-monto" autoFocus placeholder="0" value={form.amount} onChange={v => onChange({ amount: v })} />
                        </div>
                        <Input label="Fecha del gasto" type="date" value={form.expense_date}
                            onChange={e => onChange({ expense_date: e.target.value })} />
                    </div>

                    <div>
                        <span className={fieldLabel}>Proveedor</span>
                        {!showNewProvider ? (
                            <div className="flex gap-2">
                                <PickerSelect className="min-w-0 flex-1" placeholder="Sin proveedor" clearable
                                    value={form.provider_id ? Number(form.provider_id) : null}
                                    onChange={v => onChange({ provider_id: v == null ? '' : String(v) })}
                                    options={providers.map(p => ({ value: p.id, label: p.name }))} />
                                <Button variant="secondary" type="button" aria-label="Nuevo proveedor" title="Nuevo proveedor" onClick={() => setShowNewProvider(true)}><Plus size={16} /></Button>
                            </div>
                        ) : (
                            <div className="flex gap-2">
                                <input autoFocus placeholder="Nombre del proveedor" aria-label="Nombre del proveedor nuevo" value={newProviderName}
                                    onChange={e => setNewProviderName(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); onAddProvider(); } }}
                                    className="h-[var(--control-height-md)] min-w-0 flex-1 rounded-[var(--radius-control)] border border-line bg-card px-3 text-[length:var(--text-sm)] text-ink outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--focus-ring)]" />
                                <Button type="button" aria-label="Crear proveedor" title="Crear proveedor" onClick={onAddProvider}><Check size={16} /></Button>
                                <Button variant="ghost" type="button" aria-label="Cancelar proveedor nuevo" title="Cancelar" onClick={() => { setShowNewProvider(false); setNewProviderName(''); }}><X size={16} /></Button>
                            </div>
                        )}
                    </div>

                    <div>
                    <span className={fieldLabel}>Categoría</span>
                    <ChipGroup label="Categoría" multiple={false} options={EXPENSE_CATEGORIES}
                        value={form.category ? [form.category] : []}
                        // En modo simple el chip elegido se puede destildar: se ignora, siempre queda una.
                        onValueChange={v => { if (v[0]) onChange({ category: v[0] }); }} />
                    </div>

                    <div>
                    <span className={fieldLabel}>Forma de pago</span>
                    <ChipGroup label="Forma de pago" multiple={false} options={PAYMENT_OPTIONS}
                        value={[form.payment_method]}
                        onValueChange={v => { if (v[0]) onChange({ payment_method: v[0] }); }} />
                    </div>

                    <Textarea label="Observaciones" placeholder="Opcional…" rows={3} value={form.notes}
                        onChange={e => onChange({ notes: e.target.value })} />

                    <div className="flex justify-end gap-2 border-t border-line pt-4">
                        <Button variant="secondary" type="button" onClick={onClose}>Cancelar</Button>
                        <Button type="submit" disabled={!form.amount}>
                            {editing ? <Check size={16} /> : <Plus size={16} />} {editing ? 'Guardar cambios' : 'Guardar gasto'}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}
